import type { TemplateInput } from '@amzn/mulberry-internal-typescript-client';
import {DEV_CLOUDFRONT_URL, LOCAL_HOSTNAME, STAGE_PROD} from 'src/constants/appConstants';
import {isLocalHost, isPreviewHost} from "src/utils/utils";
import { fetchTemplateFromPreviewAPI } from 'src/utils/previewAuth';

interface SiteManifest {
  configUrl: string;
  tosPageUrl?: string;
  faqPageUrl?: string;
  ppPageUrl?: string;
}

export const getBaseUrl = (): string => {
  // Local development: use the dev CloudFront URL since template files
  // don't exist on localhost. Matches localhost, 0.0.0.0, 127.0.0.1,
  // or the custom hostname mapped via /etc/hosts (from .env LOCAL_HOSTNAME).
  if (isLocalHost()) {
    return DEV_CLOUDFRONT_URL;
  }
  return '';
};

/**
 * Fetches the site template for the given site ID.
 * - PREVIEW mode (version specified): calls Mulberry internal API to get draft from DDB.
 * - LIVE mode: fetches manifest pointer + config file from S3 via CloudFront.
 */
export const fetchSiteConfig = async (siteId: string, version: string, isPreview?: boolean): Promise<TemplateInput> => {
  // Only allow API calls to the Mulberry internal service from the preview distribution
  // AND when preview mode is explicitly requested via ?preview=true.
  // Prevents API calls when developing the live site locally without preview intent.
  if (isPreviewHost() && isPreview) {
    try {
      return await fetchTemplateFromPreviewAPI(siteId, version);
    } catch (error: any) {
      if (error.message === 'Redirecting to Midway for authentication') {
        return new Promise(() => {});
      }
      if (isLocalHost()) {
        console.warn('Preview API call failed locally, falling back to S3:', error);
      } else {
        throw new Error(`Failed to fetch template for site: ${siteId} and Version ${version}`);
      }
    }
  }

  const baseUrl = getBaseUrl();
  const manifestPath = `${baseUrl}/sites/${siteId}/${siteId}-manifest-pointer-file`;

  const manifestResponse = await fetch(manifestPath);
  if (!manifestResponse.ok) {
    throw new Error(`Failed to fetch manifest for site: ${siteId}`);
  }
  const manifest = await manifestResponse.json() as SiteManifest;

  const configUrl = manifest.configUrl.startsWith('http')
    ? manifest.configUrl
    : `${baseUrl}/${manifest.configUrl}`;

  const templateResponse = await fetch(configUrl);
  if (!templateResponse.ok) {
    throw new Error(`Failed to fetch template for site: ${siteId}`);
  }
  return await templateResponse.json() as TemplateInput;
};

// Returns the site ID. In prod/local, uses the hostname directly.
// On non-prod deployed stacks, reads from ?site= param and persists to localStorage
// so it survives internal navigation and page reloads (e.g., after LWA login).
export const getSite = (stage: string, env: string): string => {
  if ((stage === STAGE_PROD && env === 'LIVE') || window.location.hostname === LOCAL_HOSTNAME) {
    return window.location.hostname;
  }
  const searchParams = new URLSearchParams(window.location.search);
  const siteFromUrl = searchParams.get('site');
  if (siteFromUrl) {
    localStorage.setItem('burberrySiteId', siteFromUrl);
    return siteFromUrl;
  }
  return localStorage.getItem('burberrySiteId') ?? '';
};

// Determines if the app is in preview mode.
// ?preview=true → stores and returns true. ?preview=false, ?site= without preview, or localhost → clears storage.
// No params (internal navigation/reload) → falls back to localStorage.
export const getIsPreview = (): boolean => {
  const searchParams = new URLSearchParams(window.location.search);
  const previewParam = searchParams.get('preview');
  if (previewParam === 'true') {
    localStorage.setItem('burberryPreview', 'true');
  } else if (previewParam !== null || searchParams.has('site') || isLocalHost()) {
    localStorage.removeItem('burberryPreview');
    localStorage.removeItem('burberryVersion');
  }
  const hasNoParams = window.location.search === '';
  return previewParam === 'true' || (hasNoParams && localStorage.getItem('burberryPreview') === 'true');
};

// Returns the template version. In prod live, always 'LIVE'.
// Only stores version in localStorage when in preview mode.
// ?site= without ?version= in preview → resets to 'LIVE'. No params → falls back to stored value.
export const getVersion = (stage: string, env: string): string => {
  if (stage === STAGE_PROD && env === 'LIVE') {
    return 'LIVE';
  }
  const searchParams = new URLSearchParams(window.location.search);
  const versionFromUrl = searchParams.get('version');
  const storedVersion = localStorage.getItem('burberryVersion');

  const isPreview = getIsPreview();

  if (versionFromUrl && isPreview) {
    localStorage.setItem('burberryVersion', versionFromUrl);
    return versionFromUrl;
  }
  if (searchParams.has('site') && isPreview) {
    localStorage.setItem('burberryVersion', 'LIVE');
    return 'LIVE';
  }
  return storedVersion ?? 'LIVE';
};

export const generateCSSVariables = (siteId: string, siteConfig: TemplateInput): string => {
  const v = (value: string | number | boolean | undefined | null): string =>
    value != null ? String(value) : '';

  return `
    [site-id="${siteId}"] {
      --background-color: ${v(siteConfig.backgroundColor)};
      --primary-color: ${v(siteConfig.primaryColor)};
      --secondary-color: ${v(siteConfig.secondaryColor)};
      --accent-color: ${v(siteConfig.accentColor)};
      --button-color: ${v(siteConfig.buttonColor)};
      --button-hover-color: ${v(siteConfig.buttonHoverColor)};
      --font-family: ${v(siteConfig.font)};
      --h1-font-size: ${v(siteConfig.h1FontSize)};
      --h2-font-size: ${v(siteConfig.h2FontSize)};
      --h3-font-size: ${v(siteConfig.h3FontSize)};
      --paragraph-font-size: ${v(siteConfig.paragraphFontSize)};
      --header-font-weight: ${v(siteConfig.headerFontWeight)};
      --paragraph-font-weight: ${v(siteConfig.paragraphFontWeight)};
      --text-transform: ${v(siteConfig.textTransform)};
      --nav-bar-height: ${v(siteConfig.navBarHeight)};
      --footer-height: ${v(siteConfig.footerHeight)};
      --merch-grid-columns: ${v(siteConfig.merchGridColumns)};
      --merch-grid-spacing: ${v(siteConfig.merchGridSpacing)};
      --top-promo-banner-background-color: ${v(siteConfig.topPromoBannerBackgroundColor)};
      --top-promo-banner-background-image: ${v(siteConfig.topPromoBannerBackgroundImage)};
      --top-promo-banner-height: ${v(siteConfig.topPromoBannerHeight)};
      --footer-banner-image-url: ${v(siteConfig.footerBanner?.bannerImageUrl)};
    }
  `;
};
